import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicShopAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  ShoppingCartIcon,
  MapPinIcon,
  PhoneIcon,
  MinusIcon,
  PlusIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const PublicShop = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  
  // Auto-populate form with user data if logged in
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    fulfillmentType: 'pickup',
    deliveryAddress: '',
    deliveryNotes: ''
  });
  
  // Populate form when user data is available
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        customerName: prev.customerName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        customerPhone: prev.customerPhone || user.phone || '',
        customerEmail: prev.customerEmail || user.email || ''
      }));
    }
  }, [user]);
  
  // Payment states (must be at top level, not inside conditionals)
  const [paymentInitiating, setPaymentInitiating] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadShop();
  }, [slug]);
  
  const loadShop = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const res = await publicShopAPI.getShop(slug);
      setShop(res.data.shop);
      setProducts(res.data.products || []);
    } catch (error) {
      console.error('Load shop error:', error);
      const message = error.response?.data?.message || 'Shop not found';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const updateCart = (productId, delta) => {
    setCart(prev => {
      const current = prev[productId]?.quantity || 0;
      const currentExtras = prev[productId]?.extras || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) {
        const { [productId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [productId]: { quantity: newQty, extras: currentExtras } };
    });
  };

  const updateExtras = (productId, extras) => {
    setCart(prev => {
      if (!prev[productId]) return prev;
      const numExtras = Math.max(0, parseInt(extras) || 0);
      return { ...prev, [productId]: { ...prev[productId], extras: numExtras } };
    });
  };

  const cartItems = Object.entries(cart).map(([productId, data]) => {
    const product = products.find(p => p.id === productId);
    return { product, quantity: data.quantity || 0, extras: data.extras || 0 };
  }).filter(item => item.product);

  // Calculate subtotal: trays × price + extras × (price / tray_size)
  const subtotal = cartItems.reduce((sum, item) => {
    const trayPrice = Number(item.product.price) || 0;
    const trayTotal = trayPrice * item.quantity;
    
    // For eggs, calculate extras at per-egg rate (tray price / 30)
    let extrasTotal = 0;
    if (item.product.category === 'eggs' && item.extras > 0) {
      const traySize = 30; // Standard tray size
      const pricePerEgg = trayPrice / traySize;
      extrasTotal = item.extras * pricePerEgg;
    }
    
    return sum + trayTotal + extrasTotal;
  }, 0);
  const deliveryFee = form.fulfillmentType === 'delivery' ? Number(shop?.deliveryFee || 0) : 0;
  const total = subtotal + deliveryFee;

  const handleSubmitOrder = async () => {
    if (!form.customerName || !form.customerPhone || !form.customerEmail) {
      toast.error('Please enter your name, phone number, and email');
      return;
    }
    
    // Validate phone format (Kenyan mobile: 07/01 followed by 8 digits)
    const phoneClean = form.customerPhone.replace(/\s/g, '');
    const phoneRegex = /^0[17]\d{8}$/;
    if (!phoneRegex.test(phoneClean)) {
      toast.error('Phone must be 10 digits starting with 07 or 01 (e.g., 0712345678)');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.customerEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (form.fulfillmentType === 'delivery' && !form.deliveryAddress) {
      toast.error('Please enter your delivery address');
      return;
    }

    try {
      setSubmitting(true);
      const res = await publicShopAPI.placeOrder(slug, {
        ...form,
        items: cartItems.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          extras: item.extras || 0
        }))
      });
      setOrderPlaced(res.data);
      setCart({});
      setShowCheckout(false);
    } catch (error) {
      console.error('Place order error:', error);
      toast.error(error.response?.data?.message || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Payment functions (defined before early returns)
  const initiatePayment = async () => {
    if (!orderPlaced) return;
    try {
      setPaymentInitiating(true);
      await publicShopAPI.initiatePayment(slug, orderPlaced.order.id, form.customerPhone);
      setPaymentSent(true);
      toast.success('Payment request sent to your phone!');
      // Start polling for payment status
      pollPaymentStatus();
    } catch (error) {
      console.error('Payment initiation error:', error);
      toast.error(error.response?.data?.message || 'Failed to initiate payment. Please try manual payment.');
    } finally {
      setPaymentInitiating(false);
    }
  };

  const pollPaymentStatus = async () => {
    if (!orderPlaced) return;
    let attempts = 0;
    const maxAttempts = 30; // Check for 60 seconds (every 2 seconds)
    
    const checkStatus = async () => {
      try {
        setCheckingPayment(true);
        const res = await publicShopAPI.checkPaymentStatus(slug, orderPlaced.order.id);
        if (res.data.paymentStatus === 'paid') {
          setPaymentStatus('paid');
          toast.success('Payment successful! Thank you for your order.');
          return true;
        } else if (res.data.paymentStatus === 'failed') {
          setPaymentStatus('failed');
          toast.error('Payment failed. Please try again.');
          return true;
        }
        return false;
      } catch (e) {
        console.error('Check payment status error:', e);
        return false;
      } finally {
        setCheckingPayment(false);
      }
    };

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setCheckingPayment(false);
        return;
      }
      const done = await checkStatus();
      if (!done) {
        attempts++;
        setTimeout(poll, 2000);
      }
    };

    poll();
  };

  // Early returns for loading and error states
  if (!shop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XMarkIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {errorMessage === 'This shop is currently not available' ? 'Shop Unavailable' : 'Shop Not Found'}
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            {errorMessage || 'This shop may not exist or is currently unavailable.'}
          </p>
        </div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-5 sm:p-8 text-center">
          {paymentStatus === 'paid' ? (
            <>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-green-600">Payment Successful!</h1>
              <p className="text-gray-600 mt-2 text-sm sm:text-base">
                Thank you for your order #{orderPlaced.order.orderNumber}
              </p>
              <p className="text-gray-500 mt-2 text-xs sm:text-sm">
                The seller has been notified and will contact you shortly.
              </p>
              <button
                onClick={() => { setOrderPlaced(null); setPaymentStatus(null); setPaymentSent(false); }}
                className="mt-6 w-full py-2.5 sm:py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition text-sm sm:text-base font-medium"
              >
                Place Another Order
              </button>
            </>
          ) : isLoggedIn ? (
            /* Logged-in clients: Direct to orders page for payment */
            <>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Order Placed!</h1>
              <p className="text-gray-600 mt-1.5 sm:mt-2 text-sm sm:text-base">
                Order #{orderPlaced.order.orderNumber} • KES {orderPlaced.payment.amount}
              </p>
              
              <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-xl text-left">
                <p className="text-sm text-blue-800 font-medium mb-2">
                  What happens next?
                </p>
                <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside">
                  <li>The seller will review and <strong>confirm your order</strong></li>
                  <li>You'll receive a notification when it's confirmed</li>
                  <li>You can then <strong>pay via M-Pesa or Wallet</strong> from your orders page</li>
                </ol>
              </div>
              
              <Link
                to="/client/orders"
                className="mt-5 w-full py-3 sm:py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-base sm:text-lg font-semibold flex items-center justify-center gap-2"
              >
                View My Orders
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
              
              <button
                onClick={() => { setOrderPlaced(null); setPaymentStatus(null); setPaymentSent(false); }}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                Continue Shopping
              </button>
            </>
          ) : (
            /* Non-logged-in users: Show inline M-Pesa payment */
            <>
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-green-600" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Order Placed!</h1>
              <p className="text-gray-600 mt-1.5 sm:mt-2 text-sm sm:text-base">
                Order #{orderPlaced.order.orderNumber} • KES {orderPlaced.payment.amount}
              </p>
              
              {/* Pay Now Button - Primary Action */}
              {!paymentSent ? (
                <button
                  onClick={initiatePayment}
                  disabled={paymentInitiating}
                  className="mt-5 w-full py-3 sm:py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 active:bg-green-800 transition text-base sm:text-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {paymentInitiating ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Sending to your phone...
                    </>
                  ) : (
                    <>
                      📱 Pay with M-Pesa Now
                    </>
                  )}
                </button>
              ) : (
                <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center justify-center gap-2 text-blue-700 mb-2">
                    {checkingPayment && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}
                    <span className="font-medium text-sm sm:text-base">
                      {paymentStatus === 'failed' ? 'Payment Failed' : 'Check your phone for M-Pesa prompt'}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-blue-600">
                    Enter your M-Pesa PIN to complete payment
                  </p>
                  {paymentStatus === 'failed' && (
                    <button
                      onClick={() => { setPaymentSent(false); setPaymentStatus(null); }}
                      className="mt-3 text-sm text-blue-700 underline"
                    >
                      Try again
                    </button>
                  )}
                </div>
              )}

              {/* Divider */}
              <div className="flex items-center my-4 sm:my-5">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-3 text-xs text-gray-400">or pay manually</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
          
              {/* Manual Payment Instructions - Collapsible */}
              <details className="text-left">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  View manual payment instructions
                </summary>
                <div className="mt-3 bg-gray-50 rounded-xl p-3 sm:p-4">
                  <div className="space-y-1.5 text-xs sm:text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Paybill:</span>
                      <span className="font-mono font-semibold">{orderPlaced.payment.paybillNumber}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Account:</span>
                      <span className="font-mono font-semibold">{orderPlaced.payment.accountNumber}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Amount:</span>
                      <span className="font-bold">KES {orderPlaced.payment.amount}</span>
                    </div>
                  </div>
                </div>
              </details>

              <p className="text-xs text-gray-400 mt-4">
                The seller will be notified once payment is confirmed.
              </p>

              <button
                onClick={() => { setOrderPlaced(null); setPaymentStatus(null); setPaymentSent(false); }}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to shop
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-green-600 to-emerald-600 text-white overflow-hidden">
        {/* Shop Banner Image */}
        {shop.imageUrl && (
          <div className="absolute inset-0">
            <img
              src={shop.imageUrl}
              alt={shop.shopName}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70"></div>
          </div>
        )}
        <div className="relative max-w-4xl mx-auto px-4 py-6 sm:py-8">
          <h1 className="text-2xl sm:text-3xl font-bold">{shop.shopName}</h1>
          {shop.farmLocation && (
            <p className="flex items-center text-green-100 mt-1.5 sm:mt-2 text-sm sm:text-base">
              <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">{shop.farmLocation}</span>
            </p>
          )}
          {shop.description && (
            <p className="text-green-100 mt-1.5 sm:mt-2 text-sm sm:text-base line-clamp-2">{shop.description}</p>
          )}
          {shop.contactPhone && (
            <a href={`tel:${shop.contactPhone}`} className="inline-flex items-center text-green-100 mt-1.5 sm:mt-2 text-sm sm:text-base hover:text-white transition">
              <PhoneIcon className="h-4 w-4 mr-1 flex-shrink-0" />
              {shop.contactPhone}
            </a>
          )}
        </div>
      </div>

      {/* Not accepting orders banner */}
      {!shop.acceptingOrders && (
        <div className="bg-yellow-50 border-b border-yellow-200 py-2.5 sm:py-3 px-4 text-center">
          <p className="text-yellow-800 text-sm sm:text-base">This shop is currently not accepting orders.</p>
        </div>
      )}

      {/* Products */}
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8 pb-24 sm:pb-8">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Products</h2>
        
        {products.length === 0 ? (
          <div className="text-center py-10 sm:py-12 bg-white rounded-xl">
            <p className="text-gray-500 text-sm sm:text-base">No products available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {products.map((product) => (
              <div key={product.id} className="bg-white rounded-xl border overflow-hidden">
                {/* Product Image */}
                {product.imageUrl && (
                  <div className="h-36 sm:h-44 bg-gray-100">
                    <img 
                      src={product.imageUrl} 
                      alt={product.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{product.name}</h3>
                      {product.description && (
                        <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1 line-clamp-2">{product.description}</p>
                      )}
                      <p className="mt-1.5 sm:mt-2">
                        <span className="text-lg sm:text-xl font-bold text-gray-900">KES {product.price}</span>
                        <span className="text-xs sm:text-sm text-gray-500 ml-1">/ {product.unit}</span>
                      </p>
                    </div>
                    {shop.acceptingOrders && product.inStock && (
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          {cart[product.id]?.quantity ? (
                            <>
                              <button
                                onClick={() => updateCart(product.id, -1)}
                                className="p-1.5 sm:p-2 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300"
                              >
                                <MinusIcon className="h-4 w-4" />
                              </button>
                              <span className="w-6 sm:w-8 text-center font-semibold text-sm sm:text-base">{cart[product.id]?.quantity || 0}</span>
                              <button
                                onClick={() => updateCart(product.id, 1)}
                                className="p-1.5 sm:p-2 rounded-full bg-primary-100 text-primary-600 hover:bg-primary-200 active:bg-primary-300"
                              >
                                <PlusIcon className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => updateCart(product.id, 1)}
                              className="px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 active:bg-primary-800 text-xs sm:text-sm font-medium"
                            >
                              Add
                            </button>
                          )}
                        </div>
                        {/* Extras input for egg products */}
                        {cart[product.id]?.quantity > 0 && product.category === 'eggs' && (
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-gray-500">+ Extra eggs:</span>
                            <input
                              type="number"
                              min="0"
                              max="29"
                              value={cart[product.id]?.extras || 0}
                              onChange={(e) => updateExtras(product.id, e.target.value)}
                              className="w-12 px-1 py-0.5 text-center border rounded text-xs"
                              placeholder="0"
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {!product.inStock && (
                      <span className="text-xs sm:text-sm text-red-500 flex-shrink-0">Out of stock</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Floating Cart Button */}
      {Object.keys(cart).length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 sm:bottom-4 sm:left-auto sm:right-4 sm:max-w-sm p-3 sm:p-0 bg-white sm:bg-transparent border-t sm:border-0 shadow-lg sm:shadow-none z-40">
          <button
            onClick={() => setShowCheckout(true)}
            className="w-full bg-primary-600 text-white rounded-xl py-3 sm:py-4 px-4 sm:px-6 shadow-lg hover:bg-primary-700 active:bg-primary-800 transition flex items-center justify-between"
          >
            <div className="flex items-center">
              <ShoppingCartIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              <span className="font-semibold text-sm sm:text-base">{cartItems.reduce((sum, i) => sum + i.quantity, 0)} items</span>
            </div>
            <span className="font-bold text-sm sm:text-base">KES {subtotal.toLocaleString()}</span>
          </button>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between z-10">
              <h2 className="text-base sm:text-lg font-bold">Checkout</h2>
              <button onClick={() => setShowCheckout(false)} className="p-1 -mr-1 hover:bg-gray-100 rounded-full">
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Cart Summary */}
              <div className="space-y-1.5 sm:space-y-2">
                {cartItems.map((item) => {
                  const trayPrice = Number(item.product.price) || 0;
                  const trayTotal = trayPrice * item.quantity;
                  const extrasTotal = (item.product.category === 'eggs' && item.extras > 0) 
                    ? Math.round(item.extras * (trayPrice / 30) * 100) / 100 
                    : 0;
                  const itemTotal = trayTotal + extrasTotal;
                  
                  return (
                    <div key={item.product.id} className="text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">{item.quantity}x {item.product.name}</span>
                        <span className="font-medium">KES {trayTotal.toLocaleString()}</span>
                      </div>
                      {item.extras > 0 && item.product.category === 'eggs' && (
                        <div className="flex justify-between text-gray-500 pl-4">
                          <span>+ {item.extras} extra eggs @ KES {(trayPrice / 30).toFixed(2)}/egg</span>
                          <span>KES {extrasTotal.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {deliveryFee > 0 && (
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-700">Delivery Fee</span>
                    <span className="font-medium">KES {deliveryFee}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-base sm:text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>KES {total.toLocaleString()}</span>
                </div>
              </div>

              {/* Customer Info */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <input
                    type="text"
                    value={form.customerName}
                    onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                    className="input w-full text-sm"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">M-Pesa Phone Number *</label>
                  <input
                    type="tel"
                    value={form.customerPhone}
                    onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                    className="input w-full text-sm"
                    placeholder="0712345678"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Format: <span className="font-medium">07XXXXXXXX</span> or <span className="font-medium">01XXXXXXXX</span> (10 digits, no spaces or +254)
                  </p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={form.customerEmail}
                    onChange={(e) => setForm({ ...form, customerEmail: e.target.value })}
                    className="input w-full text-sm"
                    placeholder="john@example.com"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Required for order confirmation and updates</p>
                </div>

                {/* Fulfillment Type */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">How would you like to receive your order?</label>
                  <div className="flex gap-2 sm:gap-3">
                    {shop.pickupAvailable && (
                      <label className={`flex-1 p-2.5 sm:p-3 border rounded-lg cursor-pointer transition ${form.fulfillmentType === 'pickup' ? 'border-primary-500 bg-primary-50' : 'hover:border-gray-300'}`}>
                        <input
                          type="radio"
                          name="fulfillmentType"
                          value="pickup"
                          checked={form.fulfillmentType === 'pickup'}
                          onChange={(e) => setForm({ ...form, fulfillmentType: e.target.value })}
                          className="sr-only"
                        />
                        <span className="font-medium text-sm sm:text-base">Pickup</span>
                        <p className="text-xs text-gray-500">Free</p>
                      </label>
                    )}
                    {shop.deliveryAvailable && (
                      <label className={`flex-1 p-2.5 sm:p-3 border rounded-lg cursor-pointer transition ${form.fulfillmentType === 'delivery' ? 'border-primary-500 bg-primary-50' : 'hover:border-gray-300'}`}>
                        <input
                          type="radio"
                          name="fulfillmentType"
                          value="delivery"
                          checked={form.fulfillmentType === 'delivery'}
                          onChange={(e) => setForm({ ...form, fulfillmentType: e.target.value })}
                          className="sr-only"
                        />
                        <span className="font-medium text-sm sm:text-base">Delivery</span>
                        <p className="text-xs text-gray-500">KES {shop.deliveryFee || 0}</p>
                      </label>
                    )}
                  </div>
                </div>

                {form.fulfillmentType === 'delivery' && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Delivery Address *</label>
                    <textarea
                      value={form.deliveryAddress}
                      onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
                      className="input w-full text-sm"
                      rows={2}
                      placeholder="Enter your delivery address"
                    />
                  </div>
                )}

                {form.fulfillmentType === 'pickup' && shop.pickupAddress && (
                  <div className="p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs sm:text-sm text-gray-600">
                      <strong>Pickup location:</strong><br />
                      {shop.pickupAddress}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                  <textarea
                    value={form.deliveryNotes}
                    onChange={(e) => setForm({ ...form, deliveryNotes: e.target.value })}
                    className="input w-full text-sm"
                    rows={2}
                    placeholder="Any special instructions?"
                  />
                </div>
              </div>

              <button
                onClick={handleSubmitOrder}
                disabled={submitting || !form.customerName || !form.customerPhone || !form.customerEmail}
                className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {submitting ? 'Placing Order...' : `Place Order • KES ${total.toLocaleString()}`}
              </button>

              <p className="text-xs text-gray-500 text-center pb-2">
                After placing your order, you'll receive payment instructions.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicShop;

